import { useEffect, useRef, useContext, type JSX } from 'react';
import { PaymentInfo } from '@repo/types';
import { useLocale } from '@repo/i18n';
import { errorLog } from '@repo/common-utils/Logger';
import { MountPointContext } from '@repo/common-utils/mountPoint';

interface AdyenPaymentProps {
    context: PaymentInfo;
    onPaymentCompleted: (paymentInfo: PaymentInfo, paymentGatewayResponse: any) => void;
    onPaymentFailed?: (error: any) => void;
}

function getAdyenLocale(preferredLocale: string): string {
    switch (preferredLocale) {
        case 'dk':
        case 'da-DK':
            return 'da-DK';

        case 'se':
        case 'sv-SE':
            return 'sv-SE';

        case 'no':
        case 'nb':
        case 'nn':
        case 'nn-NO':
        case 'nb-NO':
            return 'no-NO';

        case 'de':
        case 'de-DE':
            return 'de-DE';

        case 'pl':
        case 'pl-PL':
            return 'pl-PL';

        case 'fr':
        case 'fr-FR':
            return 'fr-FR';

        case 'es':
        case 'es-ES':
            return 'es-ES';

        case 'nl':
        case 'nl-NL':
            return 'nl-NL';

        case 'fi':
        case 'fi-FI':
            return 'fi-FI';

        default:
            return 'en-GB';
    }
}

function getCountryCodeFromLocale(preferredLocale: string): string {
    switch (preferredLocale) {
        case 'dk':
        case 'da-DK':
            return 'DK';

        case 'se':
        case 'sv-SE':
            return 'SE';

        case 'no':
        case 'nb':
        case 'nn':
        case 'nn-NO':
        case 'nb-NO':
            return 'NO';

        case 'de':
        case 'de-DE':
            return 'DE';

        case 'pl':
        case 'pl-PL':
            return 'PL';

        case 'fr':
        case 'fr-FR':
            return 'FR';

        case 'es':
        case 'es-ES':
            return 'ES';

        case 'nl':
        case 'nl-NL':
            return 'NL';

        case 'fi':
        case 'fi-FI':
            return 'FI';

        default:
            return 'NO'; // Default to Norway
    }
}

export default function AdyenPayment(props: AdyenPaymentProps): JSX.Element {
    const { locale } = useLocale();
    const mountPoint = useContext(MountPointContext);
    const dropinContainerRef = useRef<HTMLDivElement>(null);
    const checkoutRef = useRef<any>(null);
    const styleElementRef = useRef<HTMLStyleElement | null>(null);

    useEffect(() => {
        let isMounted = true;

        async function initAdyen() {
            const { adyen } = props.context;
            if (!adyen) {
                throw new Error('Adyen configuration missing from PaymentInfo');
            }

            // Dynamic import to lazy-load Adyen SDK
            const { AdyenCheckout, Dropin, Card } = await import('@adyen/adyen-web');
            // Import CSS as inline string to inject into shadow root
            const adyenCssModule = await import('@adyen/adyen-web/styles/adyen.css?inline');
            const adyenCss = adyenCssModule.default;

            if (!isMounted || !dropinContainerRef.current) {
                return;
            }

            // Inject Adyen CSS into shadow root (required for Shadow DOM environments)
            if (mountPoint.shadowRoot && !styleElementRef.current) {
                const styleElement = document.createElement('style');
                styleElement.textContent = adyenCss;
                mountPoint.shadowRoot.appendChild(styleElement);
                styleElementRef.current = styleElement;
            }

            const checkout = await AdyenCheckout({
                environment: adyen.environment,
                clientKey: adyen.clientKey,
                session: {
                    id: adyen.sessionId,
                    sessionData: adyen.sessionData,
                },
                locale: getAdyenLocale(locale),
                countryCode: getCountryCodeFromLocale(locale),
                onPaymentCompleted: (result: any) => {
                    if (result.resultCode === 'Authorised' || result.resultCode === 'Pending') {
                        props.onPaymentCompleted(props.context, result);
                    } else {
                        props.onPaymentFailed?.(result);
                    }
                },
                onPaymentFailed: (result: any) => {
                    props.onPaymentFailed?.(result);
                },
                onError: (error: any) => {
                    errorLog('Adyen error:', error);
                },
            });

            checkoutRef.current = checkout;

            // Mount Drop-in to container element directly (avoids getElementById issues in Shadow DOM)
            // Pass payment method components explicitly (required in Adyen Web SDK v6+)
            new Dropin(checkout, {
                paymentMethodComponents: [Card],
            }).mount(dropinContainerRef.current);
        }

        initAdyen().catch((error) => {
            errorLog('Failed to initialize Adyen:', error);
        });

        return () => {
            isMounted = false;
            // Adyen checkout cleanup
            if (checkoutRef.current?.unmount) {
                checkoutRef.current.unmount();
            }
            // Remove injected style element
            if (styleElementRef.current) {
                styleElementRef.current.remove();
                styleElementRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- Intentionally run only once to avoid re-initializing checkout
    }, []);

    return <div ref={dropinContainerRef} />;
}
